import { SMRIQ_PRODUCT_TYPE_KEY } from '@features/service-plans/utils/isSmriqProduct';

export type ProductDetailTab =
    | 'details'
    | 'plans'
    | 'smriq-credentials'
    | 'add-ons'
    | 'add-on-groups'
    | 'config-options'
    | 'rules'
    | 'fees'
    | 'security-groups'
    | 'documents';

export interface ProductDetailTabDefinition {
    id: ProductDetailTab;
    label: string;
}

const BASE_TABS: ProductDetailTabDefinition[] = [
    { id: 'details', label: 'Details' },
    { id: 'plans', label: 'Plans' },
    { id: 'add-ons', label: 'Optional Extras' },
    { id: 'add-on-groups', label: 'Optional Extra Groups' },
    { id: 'config-options', label: 'Config Settings' },
    { id: 'rules', label: 'Product Rules' },
    { id: 'fees', label: 'Fees' },
    { id: 'security-groups', label: 'Security Groups' },
];

const DOCUMENTS_TAB: ProductDetailTabDefinition = { id: 'documents', label: 'Documents' };

// Every id `getProductDetailTabs` can ever produce, for either variant — used to
// sanity-check a `?tab=` search param before the product (and so its type) has loaded.
export const ALL_PRODUCT_DETAIL_TAB_IDS: ProductDetailTab[] = [
    ...BASE_TABS.map((tab) => tab.id),
    'smriq-credentials',
    'documents',
];

const REPLACED_TAB_IDS: Partial<Record<ProductDetailTab, ProductDetailTab>> = {
    plans: 'smriq-credentials',
};

/**
 * A `smriq_service_plan` product has no local Plans to manage, so its Plans tab is
 * replaced in place by the SMRiQ tab; every other tab is unchanged.
 */
export function getProductDetailTabs(
    productTypeKey?: string | null,
    { includeDocuments = false }: { includeDocuments?: boolean } = {},
): ProductDetailTabDefinition[] {
    const base =
        productTypeKey === SMRIQ_PRODUCT_TYPE_KEY
            ? BASE_TABS.map((tab): ProductDetailTabDefinition => (tab.id === 'plans' ? { id: 'smriq-credentials', label: 'SMRiQ' } : tab))
            : BASE_TABS;

    return includeDocuments ? [...base, DOCUMENTS_TAB] : base;
}

/**
 * A product's type is mutable, so `?tab=plans` links to a product later converted to
 * SMRiQ still exist. Resolve them onto the tab that replaced it rather than to Details.
 */
export function resolveProductDetailTab(
    requested: ProductDetailTab,
    tabs: ProductDetailTabDefinition[],
): ProductDetailTab {
    if (tabs.some((tab) => tab.id === requested)) return requested;

    const replacement = REPLACED_TAB_IDS[requested];

    if (replacement && tabs.some((tab) => tab.id === replacement)) return replacement;

    return 'details';
}
